import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { Botao } from './Botao';
import { Campo, CampoDeTexto } from './Campo';
import { Modal } from './Modal';

/**
 * Diálogos do sistema — o substituto de `window.confirm`, `window.prompt` e
 * `window.alert`.
 *
 * ## Por que não o diálogo do navegador
 *
 * O `confirm()` nativo trava a aba inteira, ignora o tema, não aceita título
 * nem botão com nome ("OK" para apagar um contrato?), e o Chrome esconde o
 * texto quando a página está em iframe ou já mostrou muitos. O usuário pediu
 * que toda confirmação fosse do próprio sistema, e um teste de guarda
 * (`dialogo.guarda.test.ts`) impede que o nativo volte.
 *
 * ## Como usar
 *
 * ```tsx
 * const { confirmar, pedirTexto } = useDialogo();
 *
 * if (await confirmar({ titulo: 'Remover o agendamento?', tom: 'perigo' })) {
 *   remover.mutate(id);
 * }
 *
 * const motivo = await pedirTexto({ titulo: 'Cancelar o envio', rotulo: 'Motivo' });
 * if (motivo !== null) cancelar.mutate({ id, motivo });
 * ```
 *
 * As funções devolvem `Promise`, como os nativos devolviam valor — a tela
 * escreve `if (await confirmar(...))` no lugar de `if (window.confirm(...))`.
 *
 * ## Um diálogo por vez
 *
 * O provedor renderiza um único `Modal`. Se uma segunda chamada chega enquanto
 * há um diálogo aberto, a anterior é resolvida como cancelada e a nova toma o
 * lugar. A alternativa — enfileirar — faria o usuário responder a uma pergunta
 * feita por uma tela que talvez já nem esteja mais montada; cancelar é o
 * resultado que nenhum código trata como "pode seguir".
 *
 * A lógica de estado vive em `criarBalcaoDeDialogos`, sem DOM, para ser
 * testável no ambiente `node` da suíte.
 */

export type TomDeDialogo = 'padrao' | 'perigo';

export interface OpcoesDeConfirmacao {
  readonly titulo: string;
  /** Aceita quebras de linha (`\n\n` vira parágrafo). */
  readonly descricao?: string;
  /** Padrão: "Confirmar". */
  readonly rotuloConfirmar?: string;
  /** Padrão: "Cancelar". */
  readonly rotuloCancelar?: string;
  /** `perigo` pinta o botão de confirmar em vermelho — para apagar, cancelar contrato, rejeitar. */
  readonly tom?: TomDeDialogo;
}

export interface OpcoesDePedidoDeTexto {
  readonly titulo: string;
  readonly descricao?: string;
  /** Rótulo do campo de texto. */
  readonly rotulo: string;
  readonly valorInicial?: string;
  /** Com `obrigatorio`, o botão fica desabilitado enquanto o texto está vazio. */
  readonly obrigatorio?: boolean;
  /** `textarea` em vez de `input`; Enter quebra linha em vez de confirmar. */
  readonly multilinha?: boolean;
  /** Padrão: "Confirmar". */
  readonly rotuloConfirmar?: string;
}

interface Pedido<T> {
  /** Sequencial. Vira a `key` do componente: pedido novo, campo de texto limpo. */
  readonly id: number;
  readonly resolver: (valor: T) => void;
  /** Resolve a promessa com o valor de "fechou sem responder" — `false` ou `null`. */
  readonly desistir: () => void;
}

export type PedidoDeDialogo =
  | ({ readonly tipo: 'confirmar'; readonly opcoes: OpcoesDeConfirmacao } & Pedido<boolean>)
  | ({ readonly tipo: 'texto'; readonly opcoes: OpcoesDePedidoDeTexto } & Pedido<string | null>);

export interface BalcaoDeDialogos {
  confirmar: (opcoes: OpcoesDeConfirmacao) => Promise<boolean>;
  pedirTexto: (opcoes: OpcoesDePedidoDeTexto) => Promise<string | null>;
  /** O pedido aberto, ou `null`. Estável entre chamadas sem mudança (para `useSyncExternalStore`). */
  atual: () => PedidoDeDialogo | null;
  /** Fecha `pedido` com `valor`. Ignorado se ele já não é o aberto — clique tardio não responde por outro. */
  concluir: <T>(pedido: PedidoDeDialogo & Pedido<T>, valor: T) => void;
  /** Fecha o pedido aberto como cancelado (Esc, clique fora, botão Cancelar). */
  cancelar: () => void;
  assinar: (ouvinte: () => void) => () => void;
}

/**
 * O estado do diálogo, separado do React: um pedido aberto por vez e as
 * promessas que cada chamada devolve.
 */
export function criarBalcaoDeDialogos(): BalcaoDeDialogos {
  let atual: PedidoDeDialogo | null = null;
  let proximoId = 1;
  const ouvintes = new Set<() => void>();

  const notificar = (): void => {
    for (const ouvinte of ouvintes) ouvinte();
  };

  const abrir = (pedido: PedidoDeDialogo): void => {
    // A anterior não fica pendurada para sempre: quem a esperava recebe
    // "cancelado" e segue o fluxo de desistência, que toda tela já trata.
    const anterior = atual;
    atual = pedido;
    notificar();
    if (anterior !== null) anterior.desistir();
  };

  const concluir = <T,>(pedido: PedidoDeDialogo & Pedido<T>, valor: T): void => {
    if (pedido !== atual) return;
    atual = null;
    notificar();
    pedido.resolver(valor);
  };

  return {
    confirmar: (opcoes) =>
      new Promise<boolean>((resolver) => {
        abrir({
          id: proximoId++,
          tipo: 'confirmar',
          opcoes,
          resolver,
          desistir: () => resolver(false),
        });
      }),
    pedirTexto: (opcoes) =>
      new Promise<string | null>((resolver) => {
        abrir({
          id: proximoId++,
          tipo: 'texto',
          opcoes,
          resolver,
          desistir: () => resolver(null),
        });
      }),
    atual: () => atual,
    concluir,
    cancelar: () => {
      const pedido = atual;
      if (pedido === null) return;
      atual = null;
      notificar();
      pedido.desistir();
    },
    assinar: (ouvinte) => {
      ouvintes.add(ouvinte);
      return () => {
        ouvintes.delete(ouvinte);
      };
    },
  };
}

export interface Dialogos {
  /** Resolve `true` no confirmar; `false` no cancelar, no Esc e no clique fora. */
  readonly confirmar: (opcoes: OpcoesDeConfirmacao) => Promise<boolean>;
  /** Resolve o texto (sem espaços nas pontas), ou `null` ao cancelar. */
  readonly pedirTexto: (opcoes: OpcoesDePedidoDeTexto) => Promise<string | null>;
}

const ContextoDeDialogos = createContext<Dialogos | null>(null);

export function useDialogo(): Dialogos {
  const dialogos = useContext(ContextoDeDialogos);
  if (dialogos === null) {
    throw new Error(
      'useDialogo precisa estar dentro de <ProvedorDeDialogos> — ele é montado em App.tsx.',
    );
  }
  return dialogos;
}

export function ProvedorDeDialogos(props: { readonly children: ReactNode }): JSX.Element {
  const [balcao] = useState(criarBalcaoDeDialogos);
  const pedido = useSyncExternalStore(balcao.assinar, balcao.atual, balcao.atual);

  // Quem chamou estava com o foco no botão que clicou. Guardado aqui, na
  // chamada, porque num efeito já seria tarde: o `showModal()` do Modal roda
  // antes e o foco já estaria dentro do diálogo.
  const origem = useRef<Element | null>(null);
  const dialogos = useMemo<Dialogos>(
    () => ({
      confirmar: (opcoes) => {
        origem.current ??= document.activeElement;
        return balcao.confirmar(opcoes);
      },
      pedirTexto: (opcoes) => {
        origem.current ??= document.activeElement;
        return balcao.pedirTexto(opcoes);
      },
    }),
    [balcao],
  );

  return (
    <ContextoDeDialogos.Provider value={dialogos}>
      {props.children}
      <JanelaDeDialogo pedido={pedido} balcao={balcao} origem={origem} />
    </ContextoDeDialogos.Provider>
  );
}

/**
 * O único `Modal` do provedor.
 *
 * Fica montado depois do primeiro uso, fechando e abrindo via `aberto`: o
 * `<dialog>` nativo devolve o foco ao elemento que o abriu quando é fechado
 * com `close()`, mas não quando é arrancado do DOM. Por isso o último pedido
 * fica guardado — é o que aparece durante o fechamento.
 */
function JanelaDeDialogo(props: {
  readonly pedido: PedidoDeDialogo | null;
  readonly balcao: BalcaoDeDialogos;
  readonly origem: MutableRefObject<Element | null>;
}): JSX.Element | null {
  const { pedido, balcao, origem } = props;
  const ultimo = useRef<PedidoDeDialogo | null>(null);
  if (pedido !== null) ultimo.current = pedido;
  const exibido = pedido ?? ultimo.current;

  // O `<dialog>` devolve o foco sozinho ao fechar. Este é o reforço para o
  // caso em que ele não consegue: um segundo pedido por cima do primeiro
  // troca o elemento `<dialog>` (a `key` muda), e o novo só sabe devolver o
  // foco para onde ele estava ao abrir — dentro do anterior, já removido.
  useEffect(() => {
    if (pedido !== null) return;
    const alvo = origem.current;
    origem.current = null;
    if (
      alvo instanceof HTMLElement &&
      alvo.isConnected &&
      document.activeElement === document.body
    ) {
      alvo.focus();
    }
  }, [pedido, origem]);

  const cancelar = useCallback(() => balcao.cancelar(), [balcao]);

  if (exibido === null) return null;

  return exibido.tipo === 'confirmar' ? (
    <Confirmacao
      key={exibido.id}
      aberto={pedido !== null}
      pedido={exibido}
      aoCancelar={cancelar}
      aoConfirmar={() => balcao.concluir(exibido, true)}
    />
  ) : (
    <PedidoDeTexto
      key={exibido.id}
      aberto={pedido !== null}
      pedido={exibido}
      aoCancelar={cancelar}
      aoConfirmar={(texto) => balcao.concluir(exibido, texto)}
    />
  );
}

function Confirmacao(props: {
  readonly aberto: boolean;
  readonly pedido: Extract<PedidoDeDialogo, { tipo: 'confirmar' }>;
  readonly aoCancelar: () => void;
  readonly aoConfirmar: () => void;
}): JSX.Element {
  const { opcoes } = props.pedido;
  const botaoCancelar = useRef<HTMLButtonElement>(null);

  // O foco nasce na ação segura: quem aperta Enter por reflexo cancela, não
  // apaga. O efeito roda depois do `showModal()` do Modal (efeito do filho
  // corre antes do pai), então o botão já está visível e focável.
  useEffect(() => {
    if (props.aberto) botaoCancelar.current?.focus();
  }, [props.aberto]);

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoCancelar}
      titulo={opcoes.titulo}
      rodape={
        <>
          <Botao ref={botaoCancelar} variante="secundario" onClick={props.aoCancelar}>
            {opcoes.rotuloCancelar ?? 'Cancelar'}
          </Botao>
          <Botao
            variante={opcoes.tom === 'perigo' ? 'perigo' : 'primario'}
            onClick={props.aoConfirmar}
          >
            {opcoes.rotuloConfirmar ?? 'Confirmar'}
          </Botao>
        </>
      }
    >
      {opcoes.descricao !== undefined ? <Descricao texto={opcoes.descricao} /> : null}
    </Modal>
  );
}

function PedidoDeTexto(props: {
  readonly aberto: boolean;
  readonly pedido: Extract<PedidoDeDialogo, { tipo: 'texto' }>;
  readonly aoCancelar: () => void;
  readonly aoConfirmar: (texto: string) => void;
}): JSX.Element {
  const { opcoes } = props.pedido;
  const [texto, setTexto] = useState(opcoes.valorInicial ?? '');
  const campoDeLinha = useRef<HTMLInputElement>(null);
  const campoDeTexto = useRef<HTMLTextAreaElement>(null);

  const limpo = texto.trim();
  const podeConfirmar = opcoes.obrigatorio !== true || limpo !== '';
  const confirmar = (): void => {
    if (podeConfirmar) props.aoConfirmar(limpo);
  };

  useEffect(() => {
    if (props.aberto) (campoDeLinha.current ?? campoDeTexto.current)?.focus();
  }, [props.aberto]);

  const rodape = (
    <>
      <Botao variante="secundario" onClick={props.aoCancelar}>
        Cancelar
      </Botao>
      <Botao onClick={confirmar} disabled={!podeConfirmar}>
        {opcoes.rotuloConfirmar ?? 'Confirmar'}
      </Botao>
    </>
  );

  return (
    <Modal aberto={props.aberto} aoFechar={props.aoCancelar} titulo={opcoes.titulo} rodape={rodape}>
      <div className="flex flex-col gap-4">
        {opcoes.descricao !== undefined && <Descricao texto={opcoes.descricao} />}
        {opcoes.multilinha === true ? (
          <CampoDeTexto
            ref={campoDeTexto}
            rotulo={opcoes.rotulo}
            obrigatorio={opcoes.obrigatorio}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        ) : (
          <Campo
            ref={campoDeLinha}
            rotulo={opcoes.rotulo}
            obrigatorio={opcoes.obrigatorio}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            // Enter confirma, como no `prompt()` — mas só com o botão habilitado,
            // senão o obrigatório teria uma porta dos fundos pelo teclado.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                confirmar();
              }
            }}
          />
        )}
      </div>
    </Modal>
  );
}

function Descricao(props: { readonly texto: string }): JSX.Element {
  return <p className="whitespace-pre-line text-sm text-conteudo-secundario">{props.texto}</p>;
}
